import {
	KeyenceComparisonStatus,
	KeyenceValidationEnforcement,
	KeyenceValidationOutcome,
	KeyenceValidationSeverity,
} from '../../interfaces/Quality.Interface';

export const OUTCOME_COLOR: Record<KeyenceValidationOutcome, string> = {
	Passed: 'green',
	Warning: 'gold',
	Failed: 'red',
	Skipped: 'default',
	'Parse Error': 'volcano',
	// Not a verdict yet — a large program still being parsed in the background.
	Pending: 'blue',
};

export const OUTCOME_OPTIONS = (
	Object.keys(OUTCOME_COLOR) as KeyenceValidationOutcome[]
).map((value) => ({ value, label: value }));

export const ENFORCEMENT_COLOR: Record<KeyenceValidationEnforcement, string> = {
	Accepted: 'default',
	'Accepted Invalid': 'orange',
	Refused: 'red',
	Overridden: 'purple',
};

export const ENFORCEMENT_OPTIONS = (
	Object.keys(ENFORCEMENT_COLOR) as KeyenceValidationEnforcement[]
).map((value) => ({ value, label: value }));

export const SEVERITY_COLOR: Record<KeyenceValidationSeverity, string> = {
	Error: 'red',
	Warning: 'gold',
	Info: 'blue',
};

export const SEVERITY_OPTIONS = (
	Object.keys(SEVERITY_COLOR) as KeyenceValidationSeverity[]
).map((value) => ({ value, label: value }));

export const SOURCE_OPTIONS = [
	{ value: 'modal', label: 'ERP modal' },
	{ value: 't150-proxy', label: 'T150 proxy' },
	{ value: 'sync', label: 'Device sync' },
];

/** Raw codes are shouty and mean nothing to a reader outside the parser. */
export const FINDING_CODE_LABELS: Record<string, string> = {
	NOMINAL_MISMATCH: 'Nominal mismatch',
	TOLERANCE_PLUS_MISMATCH: 'Upper tolerance mismatch',
	TOLERANCE_MINUS_MISMATCH: 'Lower tolerance mismatch',
	BALLOON_NOT_FOUND: 'Balloon not in plan',
	BALLOON_NOT_KEYENCE: 'Balloon not a Keyence dimension',
	BALLOON_WRONG_VIEW: 'Balloon on another view',
	BALLOON_WRONG_DEVICE: 'Balloon on another device',
	MISSING_FROM_PROGRAM: 'Missing from program',
	MISNAMED_LABEL: 'Misnamed measurement',
	DUPLICATE_LABEL: 'Duplicate label',
	TYPE_MISMATCH: 'Dimension type mismatch',
	PLAN_NOMINAL_BLANK: 'Plan nominal blank',
	PLAN_UNIT_MISMATCH: 'Plan unit is wrong',
	UNDECODED_MEASUREMENT: 'Undecoded measurement',
	UNSUPPORTED_FORMAT: 'Unsupported file format',
	PARSER_UNAVAILABLE: 'Parser unavailable',
	DEVICE_UNRESOLVED: 'Device unresolved',
	UNIT_MISSING: 'Plan unit missing',
};

export const findingCodeLabel = (code: string) =>
	FINDING_CODE_LABELS[code] ?? code.replace(/_/g, ' ').toLowerCase();

export const FIELD_LABELS: Record<string, string> = {
	nominal: 'Nominal',
	tolerance_plus: 'Upper tolerance',
	tolerance_minus: 'Lower tolerance',
};

export const COMPARISON_STATUS: Record<
	KeyenceComparisonStatus,
	{ label: string; color: string; hint: string }
> = {
	match: {
		label: 'Match',
		color: 'green',
		hint: 'Program and plan agree on every compared value.',
	},
	mismatch: {
		label: 'Mismatch',
		color: 'red',
		hint: 'The program measures this balloon with the wrong value.',
	},
	missing_from_program: {
		label: 'Missing',
		color: 'red',
		hint: 'The plan expects this balloon but the program never measures it.',
	},
	not_checked: {
		label: 'Not checked',
		color: 'default',
		hint: 'The parser cannot decode this dimension type, so it was not compared.',
	},
	not_in_plan: {
		label: 'Not in plan',
		color: 'volcano',
		hint: 'The program measures something the plan does not ask for on this view and device.',
	},
	misnamed: {
		label: 'Misnamed',
		color: 'orange',
		hint: 'The measurement is not named after a balloon number, so it cannot record against the plan.',
	},
	probable_rename: {
		label: 'Probably renamed',
		color: 'orange',
		hint: 'This misnamed measurement has the same nominal and tolerances as a balloon the program is missing, so the two are probably one mistake.',
	},
	duplicate: {
		label: 'Duplicate',
		color: 'orange',
		hint: 'Another measurement in the program already uses this label.',
	},
};

export const PAGE_SIZE_OPTIONS = ['25', '50', '100'];
